import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, MapPin, Users, Flame } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

export default function Leaderboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/leaderboard?limit=25").then(r => setData(r.data)).catch(() => setData({ leaders: [] }));
  }, []);

  const rankColor = (i) =>
    i === 0 ? "text-yellow-400" : i === 1 ? "text-slate-300" : i === 2 ? "text-amber-600" : "text-white/60";
  const medal = (i) => i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <Seo title="Fan Passport Leaderboard | ADN Local FanFest"
           description="Top ADN FanFest™ fans by check-ins across Jacksonville watch parties." />
      <div className="text-xs tracking-[0.3em] uppercase text-primary">FANFEST LEADERBOARD</div>
      <h1 className="font-bebas text-4xl sm:text-6xl mt-1">TOP FANS OF THE WEEK</h1>
      <p className="text-white/60 mt-2 max-w-2xl">
        Every check-in is a Fan Passport™ stamp. The more places you hit, the higher you climb.
        Watch this space for the Grand Prize drawing on July 19, 2026.
      </p>

      <div className="mt-6 flex flex-wrap gap-4 text-sm text-white/60">
        <span className="flex items-center gap-1"><Users size={14} className="text-primary"/> {data?.total_players?.length || 0} players competing</span>
        <span className="flex items-center gap-1"><Flame size={14} className="text-amber-400"/> Updates in real time</span>
      </div>

      <div className="mt-8 adn-card overflow-hidden" data-testid="leaderboard-table">
        {!data && <div className="p-8 text-white/50 text-sm">Loading…</div>}
        {data && data.leaders.length === 0 && (
          <div className="p-8 text-center text-white/50 text-sm">
            No check-ins yet. <Link to="/directory" className="text-primary underline">Be the first to check in.</Link>
          </div>
        )}
        {data?.leaders?.map((l, i) => (
          <div key={l.user_id}
            className={`flex items-center gap-4 px-4 sm:px-6 py-4 border-b border-adn-border last:border-0 ${i < 3 ? "bg-gradient-to-r from-primary/5 to-transparent" : ""}`}
            data-testid={`leaderboard-row-${i}`}>
            <div className={`font-bebas text-2xl w-14 ${rankColor(i)}`}>{medal(i)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bebas text-xl truncate flex items-center gap-2">
                {l.display_name}
                {l.current_streak >= 3 && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-orange-500/20 border border-orange-500/40 rounded text-[10px] text-orange-300"
                    title={`${l.current_streak}-day streak`}
                    data-testid={`streak-badge-${i}`}
                  >
                    <Flame size={10}/> {l.current_streak}
                  </span>
                )}
              </div>
              <div className="text-xs text-white/50 flex items-center gap-1"><MapPin size={10}/> {l.unique_businesses} venues visited</div>
            </div>
            <div className="text-right">
              <div className="font-bebas text-3xl text-primary flex items-center gap-1"><Trophy size={16}/> {l.stamps}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">stamps</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-white/50">
        Names appear as opted-in nicknames or &ldquo;First L.&rdquo; format. Want a custom name?{" "}
        <Link to="/dashboard/profile" className="text-primary underline">Set it in your profile</Link>.
        You can also opt out of the leaderboard entirely.
      </div>
    </div>
  );
}
